import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../../../../ui/LoadingSpinner';
import CustomDateRangeSelector from './CustomDateRangeSelector';
import dayjs from 'dayjs';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { RegulationBreakdownData } from '../../types/dashboard.types';
import { getInclusiveDays } from '../../utils/date.utils';
import { buildRangeQueryKey } from '../../queries';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../../redux/raptorPlatform/selectors';

interface ToggleGroupProps {
  options: {
    value: string | number;
    label: string | React.ReactNode;
  }[];
  defaultValue: string | number;
  onChange?: (value: { startDate: string; endDate: string }) => void;
  className?: string;
  buttonClassName?: string;
  activeCustomRange?: { startDate: string; endDate: string } | null;
  regulationBreakdown?: RegulationBreakdownData | null;
}

const RsDateRangeToggle: React.FC<ToggleGroupProps> = ({
  options,
  defaultValue,
  onChange,
  className = '',
  buttonClassName = '',
  activeCustomRange,
  regulationBreakdown,
}) => {
  const [selected, setSelected] = useState<string | number>(defaultValue);
  const today = dayjs().format('YYYY-MM-DD');
  const queryClient = useQueryClient();
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  const handleClick = (value: string | number) => {
    if (value === selected) return;
    setSelected(value);
    // Convert selection to start/end date
    const endDate = today;
    const num = +value;
    // Inclusive ranges: start = end - (N-1)
    const startDate =
      num === 1
        ? today // Current => today..today
        : dayjs()
            .subtract(num - 1, 'day')
            .format('YYYY-MM-DD');
    onChange?.({ startDate, endDate });
  };

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  // Check if activeCustomRange matches any of the standard options
  useEffect(() => {
    if (activeCustomRange) {
      const { startDate, endDate } = activeCustomRange;
      const today = dayjs().format('YYYY-MM-DD');

      // Check if it matches any standard range
      const matchingOption = options.find((option) => {
        const num = +option.value;
        const expectedStart =
          num === 1
            ? today
            : dayjs()
                .subtract(num - 1, 'day')
                .format('YYYY-MM-DD');
        return startDate === expectedStart && endDate === today;
      });

      if (matchingOption) {
        setSelected(matchingOption.value);
      } else {
        // It's a custom range
        setSelected(-1);
      }
    }
  }, [activeCustomRange, options]);

  // Ensure we visually select Custom when it triggers a change
  const handleCustomChange = (value: {
    startDate: string;
    endDate: string;
  }) => {
    setSelected(-1);
    onChange?.(value);
  };

  return (
    <div
      role="radiogroup"
      className={`inline-flex items-center gap-1 rounded-md bg-gray-50/50 p-0.5 shadow-sm ring-1 ring-gray-200/50 ${className}`}
    >
      {/* Active custom range display */}
      {activeCustomRange &&
        getInclusiveDays(
          activeCustomRange.startDate,
          activeCustomRange.endDate,
        ) > 1 && (
          <div className="hidden whitespace-nowrap border-r border-gray-200 bg-gray-50/50 px-3 py-1 text-xs font-medium text-blue-700 xl:flex">
            {dayjs(activeCustomRange.startDate).format('MMM DD, YYYY')} -{' '}
            {dayjs(activeCustomRange.endDate).format('MMM DD, YYYY')}
          </div>
        )}
      <div className="flex gap-1 lg:gap-2">
        {options.map((option) => {
          const isSelected = selected === option.value;
          const optionEnd = today;
          const numOpt = +option.value;
          const optionStart =
            numOpt === 1
              ? today
              : dayjs()
                  .subtract(numOpt - 1, 'day')
                  .format('YYYY-MM-DD');

          // Current range is cached under null,null in the query key
          const cacheKey = buildRangeQueryKey(
            numOpt === 1 ? null : optionStart,
            numOpt === 1 ? null : optionEnd,
            raptorPlatformId,
          );
          const hasCachedData = queryClient.getQueryData(cacheKey) != null;

          const isFetching =
            useIsFetching({
              predicate: (q) => {
                const key = q.queryKey as readonly unknown[];
                return (
                  Array.isArray(key) &&
                  key[0] === 'dashboard-data-v2' &&
                  key[1] === (numOpt === 1 ? null : optionStart) &&
                  key[2] === (numOpt === 1 ? null : optionEnd)
                );
              },
            }) > 0;

          const isGreyedOut = !hasCachedData && !isSelected && !isFetching;

          return (
            <button
              key={option.value}
              role="radio"
              aria-checked={isSelected}
              onClick={() => handleClick(option.value)}
              className={`relative flex min-w-[4rem] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded px-3 py-1 text-xs font-medium transition-all duration-200 ${
                isSelected
                  ? 'bg-mainblue text-white'
                  : isGreyedOut
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
              } ${buttonClassName}`}
            >
              <div className="flex w-full items-center justify-center">
                {isFetching ? (
                  <LoadingSpinner className="h-4 w-4 border-2" />
                ) : (
                  <span>{option.label}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom Date Range Selector */}
      <CustomDateRangeSelector
        onChange={handleCustomChange}
        isSelected={selected === -1}
        activeCustomRange={activeCustomRange}
        regulationBreakdown={regulationBreakdown}
      />
    </div>
  );
};

export default RsDateRangeToggle;
